import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import Loader from "../../../Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  useGetMeasurementByIdQuery,
  useCreateMeasurementMutation,
  useUpdateMeasurementMutation,
  useGetMeasurementsQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface MeasurementPayload {
  name: string;
  symbol?: string;
  description?: string;
}

interface MeasurementEditProps {
  measurementId?: string | number;
  compact?: boolean; // when true, omit breadcrumb/page wrapper (for modal)
  onSaved?: () => void; // optional callback parent can use to close modal / refresh
}

const MeasurementEditView: React.FC<MeasurementEditProps> = ({
  measurementId,
  compact = true,
  onSaved,
}) => {
  const isEditMode = measurementId !== undefined && measurementId !== null;
  const measurementIdStr = isEditMode ? String(measurementId) : undefined;

  const [saveError, setSaveError] = useState<string | null>(null);

  // fetch measurement detail when editing
  const {
    data: measurementData,
    isFetching: fetchingDetail,
    refetch: refetchMeasurement,
  } = useGetMeasurementByIdQuery(measurementIdStr as string, {
    skip: !isEditMode,
  });

  // to refresh list after save
  const { refetch: refetchMeasurements } = useGetMeasurementsQuery(undefined);

  const [createMeasurement, { isLoading: creating }] =
    useCreateMeasurementMutation();
  const [updateMeasurement, { isLoading: updating }] =
    useUpdateMeasurementMutation();

  const formik = useFormik<MeasurementPayload>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      symbol: "",
      description: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter measurement name"),
      symbol: Yup.string().required("Please enter symbol (e.g. pcs, kg)"),
      description: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      setSaveError(null);
      try {
        if (isEditMode && measurementId !== undefined) {
          // send shape the mutation expects: { id, data }
          const idNumber = Number(measurementId);
          const idToSend = Number.isNaN(idNumber) ? measurementIdStr : idNumber;

          await updateMeasurement({ id: idToSend, values }).unwrap();
        } else {
          await createMeasurement(values).unwrap();
        }

        // refresh list and single item
        refetchMeasurements();
        if (isEditMode) refetchMeasurement();

        // notify parent so it can close modal / refresh
        if (onSaved) onSaved();
      } catch (err: any) {
        const message =
          err?.data?.message || err?.message || "Failed to save measurement";
        console.error("Save failed:", err);
        setSaveError(String(message));
      }
    },
  });

  // populate form when editing
  useEffect(() => {
    if (isEditMode && measurementData) {
      formik.setValues({
        name: measurementData.name ?? "",
        symbol: measurementData.symbol ?? "",
        description: measurementData.description ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementData, isEditMode]);

  // show loader if editing and the single item is loading (compact = modal)
  if (isEditMode && fetchingDetail && compact) {
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {saveError && (
        <Row>
          <Col lg={12}>
            <Alert color="danger">{saveError}</Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            {!compact && (
              <CardHeader>
                <h5 className="card-title mb-0">
                  {isEditMode ? "Edit Measurement" : "Add Measurement"}
                </h5>
              </CardHeader>
            )}

            <CardBody>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <div className="mb-3">
                  <Label className="form-label" htmlFor="measurement-name">
                    Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="measurement-name"
                    name="name"
                    placeholder="e.g. Piece"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <div className="text-danger mt-1">{formik.errors.name}</div>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label className="form-label" htmlFor="measurement-symbol">
                    Symbol <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="measurement-symbol"
                    name="symbol"
                    placeholder="e.g. pcs, kg"
                    value={formik.values.symbol}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.symbol && formik.errors.symbol)}
                  />
                  {formik.touched.symbol && formik.errors.symbol ? (
                    <div className="text-danger mt-1">
                      {formik.errors.symbol}
                    </div>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label
                    className="form-label"
                    htmlFor="measurement-description"
                  >
                    Description
                  </Label>
                  <Input
                    type="textarea"
                    id="measurement-description"
                    name="description"
                    placeholder="Description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="text-end">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={creating || updating}
                  >
                    {creating || updating ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Saving...
                      </>
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default MeasurementEditView;
